import { useState, useEffect, useMemo } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useRoomList } from './useRoomList';
import { useAvatar } from '../context/AvatarContext';
import logger from '../utils/logger';

/**
 * A hook to get detailed information for a specific contact,
 * including shared conversations.
 * @param {string} contactId - The Matrix user ID of the contact.
 * @returns {{contact: object | null, sharedConversations: object[], isLoading: boolean}}
 */
export const useContact = (contactId) => {
  const client = useMatrixClient();
  const { getUserAvatar } = useAvatar();
  const [contact, setContact] = useState(null);

  // We need the full room list to find shared conversations.
  // useRoomList provides rooms and its own loading state.
  const { roomList, isLoading: isRoomListLoading } = useRoomList();

  useEffect(() => {
    if (!client || !contactId) {
      setContact(null);
      return;
    }

    let isMounted = true;

    const loadContact = async () => {
      const user = client.getUser(contactId);

      if (user) {
        const displayName = user.displayName || user.userId;

        // Get avatar from context
        const avatar = await getUserAvatar(user.userId, displayName, 96);

        // Only update state if component is still mounted
        if (isMounted) {
          setContact({
            id: user.userId,
            name: displayName,
            avatar: avatar,
          });
        }
      } else {
        // If the user is not in the store, it might be a new contact.
        // For now, we'll log a warning. A future implementation could fetch the profile.
        logger.warn(`useContact: User with ID ${contactId} not found in client store.`);
        if (isMounted) {
          setContact(null);
        }
      }
    };

    loadContact();

    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, contactId, getUserAvatar]);

  const sharedConversations = useMemo(() => {
    if (!client || !contactId || isRoomListLoading) {
      return [];
    }

    const myUserId = client.getUserId();

    // If viewing self, show all conversations the user is in.
    if (contactId === myUserId) {
      return roomList;
    }

    // Find rooms that contain both the current user and the selected contact.
    // This is a simplified check. For 1-on-1 rooms, a more robust check would
    // be to see if the room has exactly two members.
    return roomList.filter(room => {
      const matrixRoom = client.getRoom(room.id);
      if (!matrixRoom) return false;

      const members = matrixRoom.getJoinedMembers().map(m => m.userId);
      return members.includes(myUserId) && members.includes(contactId);
    });
  }, [client, contactId, roomList, isRoomListLoading]);

  // The overall loading state depends on the room list being ready.
  const isLoading = isRoomListLoading;

  return {
    contact,
    sharedConversations,
    isLoading,
  };
};

export default useContact;